/**
 * Custom hook for fetching accounts
 * Extracted from Activity.tsx to improve code organization
 */

import { useEffect, useState } from "react";
import { auth } from "../../../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  QuerySnapshot,
  Unsubscribe,
  FirestoreError,
  DocumentSnapshot,
  Timestamp,
  FieldValue,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { Account } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";

export interface UseAccountsResult {
  accounts: Account[];
  accountsLoaded: boolean;
}

export interface UseAccountsParams {
  workspaceId: string;
}

// Cache for accounts per workspace - persists across component unmounts
const accountsCache = new Map<string, {
  accounts: Account[];
  timestamp: number;
}>();

// Cache expiration time: 5 minutes
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Hook for fetching accounts in real-time
 * Uses caching to avoid reloading data when navigating back to Activity
 */
export const useAccounts = ({ workspaceId }: UseAccountsParams): UseAccountsResult => {
  // Check cache first
  const cachedData = accountsCache.get(workspaceId);
  const hasValidCache = !!(cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRY_MS);
  
  const [accounts, setAccounts] = useState<Account[]>(
    hasValidCache && cachedData ? cachedData.accounts : []
  );
  const [accountsLoaded, setAccountsLoaded] = useState<boolean>(hasValidCache);

  useEffect(() => {
    // Allow loading in demo mode, bypass auth mode, or when authenticated
    if (!workspaceId || (!auth.currentUser && !isDemoMode() && !isBypassAuthEnabled())) return;

    const accountsRef = collection(db, "workspaces", workspaceId, "accounts");
    const q = query(accountsRef, orderBy("accountName", "asc"));

    // Check cache again inside effect
    const cachedData = accountsCache.get(workspaceId);
    const hasValidCache = !!(cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRY_MS);

    // Only set loading if we don't have cached data
    if (!hasValidCache) {
      setAccountsLoaded(false);
    }

    const handleSnapshot = (snapshot: QuerySnapshot): void => {
      const accountsList: Account[] = snapshot.docs.map((docSnap: DocumentSnapshot) => {
        const data = docSnap.data();
        if (!data) {
          throw new Error("Account data is missing");
        }
        return {
          id: docSnap.id,
          userId: data.userId as string,
          accountName: data.accountName as string,
          group: data.group as import("../../../types").AccountGroup,
          amount: data.amount as number,
          currency: data.currency as string,
          description: data.description as string,
          createdAt: data.createdAt,
          isArchived: data.isArchived as boolean | undefined,
          archivedAt: data.archivedAt as Timestamp | FieldValue | undefined,
        };
      });
      
      // Update cache
      accountsCache.set(workspaceId, {
        accounts: accountsList,
        timestamp: Date.now(),
      });
      
      setAccounts(accountsList);
      setAccountsLoaded(true);
    };

    let unsubscribe: Unsubscribe | undefined;

    try {
      unsubscribe = onSnapshot(
        q,
        handleSnapshot,
        (error: FirestoreError) => {
          console.error("Error fetching accounts:", error);
          // Fallback query without orderBy
          if (error.code === "failed-precondition" || error.message?.includes("index")) {
            const fallbackQ = query(accountsRef);
            unsubscribe = onSnapshot(
              fallbackQ,
              handleSnapshot,
              (fallbackError: FirestoreError) => {
                console.error("Fallback query also failed:", fallbackError);
                setAccountsLoaded(true); // Set to true even on error to prevent infinite loading
              }
            );
          } else {
            setAccountsLoaded(true); // Set to true even on error to prevent infinite loading
          }
        }
      );
    } catch (error) {
      console.error("Query setup error:", error);
      setAccountsLoaded(true); // Set to true even on error to prevent infinite loading
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [workspaceId]);

  return {
    accounts,
    accountsLoaded,
  };
};
